import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { queueSubmission, isOnline } from "@/lib/offlineQueue";
import KioskBanner from "@/components/kiosk/KioskBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { LogoMark } from "@/components/brand/Logo";
import { Star, CheckCircle2, MonitorSmartphone, ArrowRight, CloudOff } from "lucide-react";

const StarRating = ({ value, onChange, testid }) => (
  <div className="flex items-center gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        data-testid={`star-${n}`}
        className="transition hover:scale-110"
      >
        <Star
          className={`w-9 h-9 ${n <= value ? "fill-[#D4A547] text-[#D4A547]" : "text-[#EBE3D2]"}`}
          strokeWidth={1.5}
        />
      </button>
    ))}
  </div>
);

const blankForm = () => ({
  full_name: "",
  is_anonymous: false,
  star_rating: 0,
  enjoyed_most: "",
  what_changed: "",
  would_recommend: "Yes",
  additional_comments: "",
  share_publicly: true,
});

const FeedbackPage = () => {
  const { workshopId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const kiosk = searchParams.get("kiosk") === "1";

  const [workshop, setWorkshop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {queued, sharePublicly}
  const [error, setError] = useState("");
  const [form, setForm] = useState(blankForm());
  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/public/workshop-info/${workshopId}`)
      .then((r) => setWorkshop(r.data))
      .catch(() => setError("Feedback link is invalid or expired."))
      .finally(() => setLoading(false));
  }, [workshopId]);

  useEffect(() => {
    if (!submitted || !kiosk) return;
    const t = setTimeout(() => {
      setSubmitted(null);
      setForm(blankForm());
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 5000);
    return () => clearTimeout(t);
  }, [submitted, kiosk]);

  const enterKiosk = () => {
    const sp = new URLSearchParams(searchParams);
    sp.set("kiosk", "1");
    setSearchParams(sp, { replace: true });
  };
  const exitKiosk = () => {
    const sp = new URLSearchParams(searchParams);
    sp.delete("kiosk");
    setSearchParams(sp, { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.star_rating === 0) { setError("Please give a star rating."); return; }
    if (!form.is_anonymous && !form.full_name.trim()) { setError("Enter your name or check 'Submit anonymously'."); return; }
    setSubmitting(true);

    const payload = {
      workshop_id: workshopId,
      organisation: workshop?.client_name || "",
      ...form,
    };

    if (!isOnline()) {
      queueSubmission("/public/feedback", payload, {
        type: "feedback",
        name: form.is_anonymous ? "Anonymous" : form.full_name,
      });
      setSubmitted({ queued: true, sharePublicly: form.share_publicly });
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      await api.post("/public/feedback", payload);
      setSubmitted({ sharePublicly: form.share_publicly });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const status = err?.response?.status;
      if (!status || status >= 500) {
        queueSubmission("/public/feedback", payload, {
          type: "feedback",
          name: form.is_anonymous ? "Anonymous" : form.full_name,
        });
        setSubmitted({ queued: true, sharePublicly: form.share_publicly });
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setError(err?.response?.data?.detail || "Submission failed. Please try again.");
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B5944]">Loading…</div>;

  if (error && !workshop) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFBF5]">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl text-[#A23E3E]">Link not valid</h1>
          <p className="text-[#6B5944] mt-2">{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#FDFBF5]" data-testid="feedback-success">
        {kiosk && <KioskBanner label="Kiosk Mode · Feedback" />}
        <div className="flex items-center justify-center p-6 min-h-[80vh]">
          <div className="max-w-xl text-center ccs-fade-up">
            <div className="w-16 h-16 rounded-2xl bg-[#6B8E5A]/15 flex items-center justify-center mx-auto mb-6">
              {submitted.queued ? (
                <CloudOff className="w-8 h-8 text-[#C28A2E]" strokeWidth={1.7} />
              ) : (
                <CheckCircle2 className="w-8 h-8 text-[#6B8E5A]" strokeWidth={1.7} />
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#C28A2E] font-bold">
              {submitted.queued ? "Saved offline" : "Thank you"}
            </div>
            <h1 className="font-display text-4xl font-semibold tracking-tight text-[#2A1810] mt-3">Your feedback matters</h1>
            {submitted.queued ? (
              <p className="text-[#5D3A1F] mt-4 leading-relaxed">
                We're offline — your feedback has been saved on this device and will sync automatically
                as soon as the connection returns.
              </p>
            ) : (
              <p className="text-[#5D3A1F] mt-4 leading-relaxed">
                Thank you for completing The CCS Approach programme.
                {submitted.sharePublicly && " Once Joylance approves it, your testimonial may appear on our public wall."}
              </p>
            )}

            {kiosk ? (
              <div className="mt-8 space-y-3">
                <p className="text-xs text-[#6B5944]">Auto-clearing for the next participant in a few seconds…</p>
                <Button
                  onClick={() => { setSubmitted(null); setForm(blankForm()); }}
                  data-testid="kiosk-next-participant-btn"
                  className="bg-[#5D3A1F] hover:bg-[#4A2E18] text-white rounded-xl px-6"
                >
                  Next participant <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-[#6B5944] mt-8 italic">
                "Let everything you do be done in love." — 1 Corinthians 16:14
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF5]" data-testid="public-feedback-page">
      {kiosk && <KioskBanner label="Kiosk Mode · Feedback" />}

      <header className="border-b border-[#EBE3D2] bg-[#FDFBF5]/85 backdrop-blur-md sticky top-0 z-30" style={kiosk ? { top: 44 } : undefined}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <LogoMark size={40} />
          <div className="flex-1">
            <div className="font-display text-base font-semibold text-[#2A1810]">The CCS Approach</div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#6B5944] font-medium">End-of-Programme Feedback</div>
          </div>
          {!kiosk ? (
            <Button
              variant="outline" size="sm" onClick={enterKiosk}
              className="hidden sm:inline-flex border-[#C28A2E]/40 text-[#5D3A1F] hover:bg-[#C28A2E]/10"
              data-testid="enter-kiosk-btn"
            >
              <MonitorSmartphone className="w-3.5 h-3.5 mr-1.5" /> Use this device for many participants
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={exitKiosk} className="text-[#6B5944]" data-testid="exit-kiosk-btn">
              Exit kiosk
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 lg:py-12">
        <div className="ccs-card ccs-grain p-6 lg:p-8 mb-6">
          <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-[#2A1810] leading-tight">
            Tell us how it went
          </h1>
          {workshop && (
            <p className="text-sm text-[#6B5944] mt-2">
              <strong>{workshop.name}</strong>{workshop.client_name && <> · {workshop.client_name}</>}
            </p>
          )}
          <p className="text-sm text-[#6B5944] mt-3 leading-relaxed">
            Your honest reflection helps us serve future organisations better — and helps tomorrow's participants
            decide whether The CCS Approach is right for their team.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="feedback-form">
          {/* Name + anonymous */}
          <div className="ccs-card p-5 lg:p-6 space-y-3">
            <Label className="text-sm font-medium">Your name</Label>
            <Input
              data-testid="f-name-input"
              value={form.full_name}
              onChange={(e) => setF("full_name", e.target.value)}
              disabled={form.is_anonymous}
              className="h-11 rounded-lg bg-white border-[#EBE3D2] disabled:opacity-50"
              placeholder={form.is_anonymous ? "(Submitting anonymously)" : "First and last name"}
              autoComplete="off"
            />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                data-testid="f-anonymous-toggle"
                checked={form.is_anonymous}
                onCheckedChange={(v) => setF("is_anonymous", !!v)}
              />
              <span className="text-[#2A1810]">Submit anonymously</span>
            </label>
            <div className="text-xs text-[#6B5944]">Organisation: <strong>{workshop?.client_name || "—"}</strong></div>
          </div>

          {/* Rating */}
          <div className="ccs-card p-5 lg:p-6 space-y-3">
            <Label className="text-sm font-medium">Overall, how would you rate this programme?</Label>
            <StarRating value={form.star_rating} onChange={(v) => setF("star_rating", v)} testid="f-star-rating" />
          </div>

          {[
            { key: "enjoyed_most", label: "What did you enjoy most?", rows: 3 },
            { key: "what_changed", label: "What has changed for you?", rows: 3 },
          ].map((q) => (
            <div key={q.key} className="ccs-card p-5 lg:p-6 space-y-2">
              <Label className="text-sm font-medium">{q.label}</Label>
              <Textarea data-testid={`f-${q.key}`} rows={q.rows} value={form[q.key]} onChange={(e) => setF(q.key, e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
            </div>
          ))}

          <div className="ccs-card p-5 lg:p-6 space-y-2">
            <Label className="text-sm font-medium">Would you recommend The CCS Approach?</Label>
            <Select value={form.would_recommend} onValueChange={(v) => setF("would_recommend", v)}>
              <SelectTrigger data-testid="f-recommend-select" className="h-11 rounded-lg bg-white border-[#EBE3D2]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Yes">Yes</SelectItem>
                <SelectItem value="Maybe">Maybe</SelectItem>
                <SelectItem value="No">No</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="ccs-card p-5 lg:p-6 space-y-2">
            <Label className="text-sm font-medium">Additional comments</Label>
            <Textarea data-testid="f-comments" rows={3} value={form.additional_comments} onChange={(e) => setF("additional_comments", e.target.value)} className="rounded-lg bg-white border-[#EBE3D2]" />
          </div>

          <div className="ccs-card p-5 lg:p-6 space-y-3">
            <Label className="text-sm font-medium">Sharing permission</Label>
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-[#FDFBF5]">
                <Checkbox
                  data-testid="f-share-yes"
                  checked={form.share_publicly}
                  onCheckedChange={() => setF("share_publicly", true)}
                />
                <div className="text-sm">
                  <div className="font-medium text-[#2A1810]">Yes — share publicly</div>
                  <div className="text-xs text-[#6B5944]">Joylance may feature this on the public testimonial wall after review.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded-lg hover:bg-[#FDFBF5]">
                <Checkbox
                  data-testid="f-share-no"
                  checked={!form.share_publicly}
                  onCheckedChange={() => setF("share_publicly", false)}
                />
                <div className="text-sm">
                  <div className="font-medium text-[#2A1810]">No — keep private</div>
                  <div className="text-xs text-[#6B5944]">For Joylance's eyes only.</div>
                </div>
              </label>
            </div>
          </div>

          {error && <div className="text-sm text-[#A23E3E] bg-[#A23E3E]/10 rounded-lg p-3" data-testid="feedback-error">{error}</div>}

          <Button type="submit" disabled={submitting} data-testid="f-submit-btn"
            className="w-full h-12 rounded-xl bg-[#5D3A1F] hover:bg-[#4A2E18] text-white font-medium text-base hover:-translate-y-[1px] hover:shadow-lg transition-all">
            {submitting ? "Submitting…" : (kiosk ? "Submit & Pass to Next Participant" : "Submit Feedback")}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default FeedbackPage;
